import { Chip, useTheme, type SxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

const getStatusColors = (status: string | null | undefined, theme: Theme) => {
  const isDarkMode = theme.palette.mode === 'dark';

  if (!status) {
    return {
      backgroundColor: isDarkMode
        ? 'rgba(128, 128, 128, 0.15)'
        : 'rgba(128, 128, 128, 0.12)',
      color: isDarkMode ? '#9e9e9e' : '#757575',
      border: `1px solid ${isDarkMode ? 'rgba(128, 128, 128, 0.3)' : 'rgba(128, 128, 128, 0.3)'}`
    };
  }

  switch (status.toUpperCase()) {
    // Shared statuses (Work Order and Detail)
    case 'INCOMPLETE':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(255, 152, 0, 0.15)'
          : 'rgba(255, 152, 0, 0.12)',
        color: isDarkMode ? '#ff9800' : '#f57c00',
        border: `1px solid ${isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`
      };

    case 'COMPLETE':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(76, 175, 80, 0.15)'
          : 'rgba(76, 175, 80, 0.12)',
        color: isDarkMode ? '#66bb6a' : '#4caf50',
        border: `1px solid ${isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.3)'}`
      };

    // Work Order specific statuses
    case 'INVOICED':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(33, 150, 243, 0.15)'
          : 'rgba(33, 150, 243, 0.12)',
        color: isDarkMode ? '#42a5f5' : '#2196f3',
        border: `1px solid ${isDarkMode ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.3)'}`
      };

    case 'CANCELLED':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(244, 67, 54, 0.15)'
          : 'rgba(244, 67, 54, 0.12)',
        color: isDarkMode ? '#ef5350' : '#f44336',
        border: `1px solid ${isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`
      };

    // Work Order Detail specific statuses
    case 'SKIPPED':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(158, 158, 158, 0.15)'
          : 'rgba(158, 158, 158, 0.12)',
        color: isDarkMode ? '#9e9e9e' : '#757575',
        border: `1px solid ${isDarkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.3)'}`
      };

    case 'BACKORDERED':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(156, 39, 176, 0.15)'
          : 'rgba(156, 39, 176, 0.12)',
        color: isDarkMode ? '#ba68c8' : '#9c27b0',
        border: `1px solid ${isDarkMode ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.3)'}`
      };

    default:
      return {
        backgroundColor: isDarkMode
          ? 'rgba(158, 158, 158, 0.15)'
          : 'rgba(158, 158, 158, 0.12)',
        color: isDarkMode ? '#9e9e9e' : '#757575',
        border: `1px solid ${isDarkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.3)'}`
      };
  }
};

const getColorVariantStyles = (
  color: 'warning' | 'success' | 'error',
  theme: Theme
) => {
  const isDarkMode = theme.palette.mode === 'dark';

  switch (color) {
    case 'warning':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(217, 119, 6, 0.15)'
          : 'rgba(255, 152, 0, 0.25)',
        color: isDarkMode ? '#d97706' : '#ff6b00',
        border: `1px solid ${
          isDarkMode ? 'rgba(217, 119, 6, 0.3)' : 'rgba(255, 152, 0, 0.5)'
        }`
      };
    case 'success':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(34, 197, 94, 0.15)'
          : 'rgba(34, 197, 94, 0.25)',
        color: isDarkMode ? '#22c55e' : '#16a34a',
        border: `1px solid ${
          isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.5)'
        }`
      };
    case 'error':
      return {
        backgroundColor: isDarkMode
          ? 'rgba(239, 68, 68, 0.15)'
          : 'rgba(239, 68, 68, 0.25)',
        color: isDarkMode ? '#ef4444' : '#dc2626',
        border: `1px solid ${
          isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.5)'
        }`
      };
  }
};

const getMetadataChipColors = (theme: Theme) => {
  return {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)',
    border: 'none',
    color: theme.palette.text.secondary
  };
};

interface StatusChipProps {
  label: string | null | undefined;
  variant?: 'status' | 'metadata';
  color?: 'warning' | 'success' | 'error';
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

export const StatusChip: React.FC<StatusChipProps> = ({
  label,
  variant = 'status',
  color,
  size = 'small',
  sx = {}
}) => {
  const theme = useTheme();

  const displayLabel = label || 'Unknown';

  const colors =
    variant === 'metadata'
      ? getMetadataChipColors(theme)
      : color
        ? getColorVariantStyles(color, theme)
        : getStatusColors(label, theme);

  return (
    <Chip
      label={displayLabel}
      size={size}
      sx={{
        ...colors,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: '24px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        '& .MuiChip-label': {
          padding: '0 10px'
        },
        ...(variant === 'metadata' && {
          fontWeight: 500,
          textTransform: 'none',
          letterSpacing: '0px',
          '& .MuiChip-label': {
            px: 1.5
          }
        }),
        ...sx
      }}
    />
  );
};
